import React, { useEffect } from "react";
import { Branding, BrandingImage, DisabledPrompt, Link, ModalOpen, useModel, useParams, useRoute, useStateManager, useWorkflow, BCMSStateManagerCRUD, useBCMSModal, BrandingStyle } from "bcms-react";

import { LeftColumnContainer, PageContainer, PageFooterView, RightColumnContainer } from 'components/shared';
import FormContainer, { FormRowView } from 'components/shared/containers/FormContainer';
import { CenterContentContainer, LayoutContainer } from 'components/shared/containers/PageLayoutContainer';
import { PrimaryButton } from 'components/ui/UIButton';
import UIPasswordInput from 'components/ui/UIPasswordInput';
import UITextInput from 'components/ui/UITextInput';

import './styles.css';
import { ImageAsset } from "res";

/**
 * @name LoginPage
 * @param {OBJECT} data - Data passed to the page from either a workflow or capture
 * @param {OBJECT} ui - UI passed to the page from either a workflow or capture
 * @return {React.Component}
 * @description The main component for the login page
 */
export default function LoginPage(props) {
    // Set the data and ui variables with the default values and whats passed
    // through the properties

    // "useWorkflow" is currently only set through the "ChatWorkflow" 
    // component since this page is included in that workflow.
    const workflow = useWorkflow();
    const hasWorkflow = workflow ? true : false;
    const { branding } = useModel() ?? {};
    const { nextStep } = useWorkflow() ?? {};
    const data = { ...getDefaultPageData(hasWorkflow, nextStep, branding), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    // The state that will be passed to the state manager
    const state = { data, ui };
    // :: React.state, React.setState -> OBJECT
    // Function that returns the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property in the controller
        ui: state.ui, // Set the ui property in the controller 

        // onChange :: STRING, * -> VOID
        // Update the data based on the passed prop and value parameters
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", prop ], value)
                .submit(setState);
        }
    });

    return (
        <PageContainer className="login-page" state={ state } controller={ controller }>
            <LoginContent />
            <PageFooterView />
        </PageContainer>
    )
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data that the pages needs
 */
export const getDefaultPageData = (hasWorkflow, nextStep, branding) => ({
    userID: "",
    password: "",
    branding,
    nextStep,
    hasWorkflow
});

/**
 * @name getDefaultPageUI
 * @returns {OBJECT}
 * @description Hook to return the default ui that the page needs
 */
export const getDefaultPageUI = () => ({
    modal: {
      id: undefined,
      data: undefined
    }
});

/**
 * @name LoginContent
 * @returns {React.Component}
 * @description Content component for the login page
 */
function LoginContent(props) {
    // Get the data, ui and onChange properties from the state manager
    const { data, ui, onChange } = useStateManager();
    const hasWorkflow = data.hasWorkflow;
    const { open } = useBCMSModal();
    const navigate = useRoute();

    // Once the page is loaded, if the modal property of the ui has been set
    // then open the modal
    useEffect(() => {
      if(ui.modal.id !== undefined){ open(ui.modal.id, ui.modal.data); }
    }, [ props ]);

    // onLoginClick :: EVENT -> VOID
    // This function would typically be in the "controller", but "useBCMSModal" can't be 
    // used in the "LoginPage" component since that component hasn't been initialized yet.
    const onLoginClick = evt => {
        // If this page is in a workflow, then go to the next step. 
        // Currently the only workflow component using this page is "ChatWorkflow".
        if(hasWorkflow) {
            data.nextStep();
        } else if(data.branding.options.first_time_login_abi22 === "true") {
            open("FirstTimeLoginModal");
        } else {
            navigate("/accountSummary");
        }
    };

    return (
        <div className="login-page-container flex-width flex-container flex-vertical">
            <CenterContentContainer>
                <div className="fi-logo-container">
                    {/* FI Logo here */}
                    <BrandingImage id="login_logo_abi22" className="login-header-logo"/>
                </div>
                {/* FDIC Logo here */}
                <Branding id="fdic_abi22" value="Colored (White BG)">
                    <div className="fi-logo-container">
                            <ImageAsset id="FDICDigitalSign" className="login-fdic-header-logo" />
                    </div>
                </Branding>
                <Branding id="fdic_abi22" value="White (Dark BG)">
                    <div className="fi-logo-container">
                        <ImageAsset id="FDICDigitalSignWhite" className="login-fdic-header-logo" />
                    </div>
                </Branding>
                <FormContainer>
                    <FormRowView text="User ID">
                        <UITextInput data-point="Login_UserID_Field" value={ data.userID } onChange={ onChange("userID") } />
                    </FormRowView>
                    
                    <FormRowView text="Password">
                        <UIPasswordInput data-point="Login_Password_Field" value={ data.password } onChange={ onChange("password") } />
                    </FormRowView>
                    
                    <FormRowView>
                        <PrimaryButton onClick={ onLoginClick } text="Log In" data-point="Login_Submit" />
                    </FormRowView>
                    
                    <FormRowView className="text-center">
                        <DisabledPrompt>
                            <Link to="/accountSummary">Forgot Password?</Link>
                        </DisabledPrompt>
                    </FormRowView>
                </FormContainer>

                <LayoutContainer>
                    <LeftColumnContainer>
                        <Branding id="register_abi22" exp={val => val !== "false"}>
                            <DisabledPrompt>
                                <Link to="/">Register</Link>
                            </DisabledPrompt>
                        </Branding>
                        
                        <Branding id="chat_abi22" value="true">
                            <DisabledPrompt>
                                <Link to="/">Chat</Link>
                            </DisabledPrompt>
                        </Branding>
                        
                        <Branding id="disclaimer_abi22" exp={val => val !== "false"}>
                            <DisabledPrompt>
                                <Link to="/">Disclaimer</Link>
                            </DisabledPrompt>
                        </Branding>
                    </LeftColumnContainer>
                    
                    <RightColumnContainer>
                        <Branding id="supported_browsers_abi22" exp={val => val !== "false"}>
                            <DisabledPrompt>
                                <Link to="/">Supported Browsers</Link>
                            </DisabledPrompt>
                        </Branding>
                        
                        <Branding id="privacy_policy_abi22" exp={val => val !== "false"}>
                            <DisabledPrompt>
                                <Link to="/">Privacy Policy</Link>
                            </DisabledPrompt>
                        </Branding>
                        
                        <Branding id="signin_problems_FAQ_abi22" exp={val => val !== "false"}>
                            <DisabledPrompt>
                                <Link to="/">Sign In Problems FAQ</Link>
                            </DisabledPrompt>
                        </Branding>
                    </RightColumnContainer>
                </LayoutContainer>
            </CenterContentContainer>
        </div>
    );
}